import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { SPORTS, CITIES } from "../lib/api";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Lock, Globe } from "lucide-react";

export default function CreateTeam() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    sport: "futsal",
    city: "Jakarta",
    description: "",
    visibility: "public",
    logo: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const logoRef = useRef(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus gambar"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("File terlalu besar"); return; }
    try {
      const data = await compressImage(file);
      update("logo", data);
    } catch (e) { toast.error("Gagal proses gambar"); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Isi nama team dulu"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/teams", {
        name: form.name.trim(),
        sport: form.sport,
        city: form.city,
        description: form.description || null,
        visibility: form.visibility,
        logo: form.logo || null,
      });
      toast.success("Team dibuat! 🎉");
      navigate(`/teams/${data.team_id}`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat team");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <header className="px-4 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Buat Team</div>
          <h1 className="font-heading text-2xl font-black tracking-tight">New Crew</h1>
        </div>
      </header>

      <div className="px-4 mt-4 space-y-5">
        <Field label="Logo Team (optional)">
          {form.logo ? (
            <div className="relative w-28 h-28 rounded-3xl overflow-hidden border border-border bg-secondary mx-auto">
              <img src={form.logo} alt="logo" className="w-full h-full object-cover" />
              <button onClick={() => update("logo", null)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 flex items-center justify-center" data-testid="remove-logo">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => logoRef.current?.click()}
              data-testid="pick-logo"
              className="mx-auto block w-28 h-28 rounded-3xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 hover:border-primary/40"
            >
              <Upload className="h-6 w-6 text-primary" />
              <span className="text-[10px] font-bold">Upload</span>
            </button>
          )}
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogo(e.target.files?.[0])} data-testid="logo-file-input" />
        </Field>

        <Field label="Nama Team">
          <input
            data-testid="team-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="contoh: Padel BSD Ninjas"
            maxLength={60}
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary"
          />
        </Field>

        <Field label="Olahraga">
          <div className="grid grid-cols-4 gap-2">
            {SPORTS.map((s) => (
              <button
                key={s.id}
                data-testid={`team-sport-${s.id}`}
                onClick={() => update("sport", s.id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl border ${form.sport === s.id ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}
              >
                <span className="text-xl">{s.icon}</span>
                <span className="text-[10px] font-bold">{s.label}</span>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Kota / Area">
          <select
            data-testid="team-city"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary"
          >
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Deskripsi (optional)">
          <textarea
            data-testid="team-desc"
            rows={3}
            maxLength={300}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Main bareng tiap Jumat malam di Senayan"
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3 focus:outline-none focus:border-primary resize-none"
          />
        </Field>

        <Field label="Visibility">
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="team-vis-public"
              onClick={() => update("visibility", "public")}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-2xl border font-bold text-sm ${form.visibility === "public" ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary text-foreground"}`}
            >
              <Globe className="h-4 w-4" /> Public
            </button>
            <button
              data-testid="team-vis-private"
              onClick={() => update("visibility", "private")}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-2xl border font-bold text-sm ${form.visibility === "private" ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary text-foreground"}`}
            >
              <Lock className="h-4 w-4" /> Private
            </button>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {form.visibility === "public" ? "Semua bisa lihat & join team ini." : "Hanya yang punya invite code bisa join."}
          </div>
        </Field>

        <button
          data-testid="team-submit"
          onClick={submit}
          disabled={submitting}
          className="w-full px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold glow-cyan-strong active:scale-95 disabled:opacity-50"
        >
          {submitting ? "Membuat..." : "Buat Team 🏆"}
        </button>
      </div>

      <BottomNav />
    </PageShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

async function compressImage(file) {
  const img = await fileToImage(file);
  const maxDim = 512;
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
    else { width = Math.round((width * maxDim) / height); height = maxDim; }
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.85);
}

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img); img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}
